import { useEffect, useState } from "react";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const tourSteps: Step[] = [
  {
    target: "body",
    content: (
      <div>
        <h2 className="text-xl font-bold mb-2">Welcome to Dollar Bill! 🧠</h2>
        <p className="text-sm">
          Let's take a quick tour of the key features that will help you manage your restaurant's finances effortlessly.
        </p>
      </div>
    ),
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour="integrations-section"]',
    content: (
      <div>
        <h3 className="font-semibold mb-2">Platform Integrations</h3>
        <p className="text-sm">
          Connect your Toast, Uber Eats, and DoorDash accounts here to automatically sync sales data and streamline reconciliation.
        </p>
      </div>
    ),
    placement: "bottom",
  },
  {
    target: '[data-tour="reconciliation-link"]',
    content: (
      <div>
        <h3 className="font-semibold mb-2">Sales Reconciliation</h3>
        <p className="text-sm">
          Match platform deposits with bank transactions using filters and confidence scores. This ensures your books are accurate.
        </p>
      </div>
    ),
    placement: "right",
  },
  {
    target: '[data-tour="bank-statements-link"]',
    content: (
      <div>
        <h3 className="font-semibold mb-2">Bank Statements</h3>
        <p className="text-sm">
          Upload and manage your bank statements. Our AI automatically categorizes transactions to save you time.
        </p>
      </div>
    ),
    placement: "right",
  },
  {
    target: '[data-tour="categorization-rules"]',
    content: (
      <div>
        <h3 className="font-semibold mb-2">Categorization Rules</h3>
        <p className="text-sm">
          Set up custom rules to automatically categorize bank transactions. The system learns from your corrections to improve accuracy over time.
        </p>
      </div>
    ),
    placement: "top",
  },
  {
    target: '[data-tour="receipt-uploads"]',
    content: (
      <div>
        <h3 className="font-semibold mb-2">Receipt Management</h3>
        <p className="text-sm">
          Upload receipts directly or forward them to your unique email address. Our OCR technology extracts data automatically.
        </p>
      </div>
    ),
    placement: "right",
  },
];

interface OnboardingTourProps {
  onComplete?: () => void;
}

export const OnboardingTour = ({ onComplete }: OnboardingTourProps) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("has_completed_onboarding")
        .eq("user_id", user.id)
        .single();

      if (profile && !profile.has_completed_onboarding) {
        // Small delay to ensure DOM elements are rendered
        setTimeout(() => setRun(true), 1000);
      }
    };

    checkOnboardingStatus();
  }, []);

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { status, index, action } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      
      // Mark onboarding as complete
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase
          .from("profiles")
          .update({ has_completed_onboarding: true })
          .eq("user_id", user.id);

        if (error) {
          toast({
            title: "Error",
            description: "Failed to save onboarding progress",
            variant: "destructive",
          });
        }
      }

      onComplete?.();
    } else if (action === "next" || action === "prev") {
      setStepIndex(index + (action === "next" ? 1 : -1));
    }
  };

  return (
    <Joyride
      steps={tourSteps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: "hsl(var(--primary))",
          textColor: "hsl(var(--foreground))",
          backgroundColor: "hsl(var(--background))",
          arrowColor: "hsl(var(--background))",
          overlayColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 8,
          padding: 20,
        },
        buttonNext: {
          backgroundColor: "hsl(var(--primary))",
          borderRadius: 6,
          padding: "8px 16px",
        },
        buttonBack: {
          color: "hsl(var(--muted-foreground))",
          marginRight: 10,
        },
        buttonSkip: {
          color: "hsl(var(--muted-foreground))",
        },
      }}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip Tour",
      }}
    />
  );
};
